import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import {
  ApplicantOwnAnyVehicles,
  FinalLoanApplication,
  ILoanService,
  LoanApplication,
} from './loan.model';
import {
  LivingSituationType,
  RequestCreditType,
} from '../dropdowns/dropdowns.model';
import {
  ApplicantBankingForm,
  ApplicantEmployerForm,
  ApplicantInformationForm,
  ApplicantRentForm,
} from 'src/app/models/loan-form.model';
import { ApiService } from '../api/api.service';
import { ErrorMessageService } from '../error-message/error-message.service';
import { stripPhoneNumber } from 'src/app/directives/phone-number/phone-number.directive';

@Injectable({
  providedIn: 'root',
})
export class LoanService implements ILoanService {
  finalLoanApplication = new BehaviorSubject<FinalLoanApplication | null>(null);

  /**
   * This is needed because when an applicant doesn't own a vehicle, the {OwnAnyVehicles}
   * value is `NONE`.
   */
  ApplicantOwnAnyVehicles = new BehaviorSubject<ApplicantOwnAnyVehicles | null>(
    null
  );

  /**
   * This is needed because when an co-applicant doesn't own a vehicle, the {OwnAnyVehicles}
   * value is `NONE`.
   */
  CoApplicantOwnAnyVehicles =
    new BehaviorSubject<ApplicantOwnAnyVehicles | null>(null);

  constructor(
    private api: ApiService,
    private http: HttpClient,
    private errorMessageService: ErrorMessageService
  ) {}

  /**
   * POST request for consumer loan.
   *
   * Endpoints:
   * * loan/save - Saves loan application process prior to completion.
   * * loan/skip - Used when user exits the form to finish by phone.
   * * loan/final - Submits loan application process.
   *
   * @param body Request body of type {FinalLoanApplication}.
   * @param endpoint Defaults to `loan/save`.
   * @returns Promise of type {FinalLoanApplication}. This will return the entire {FinalLoanApplication} object with IDs.
   */
  async postLoanApplication(
    body: FinalLoanApplication,
    endpoint = 'loan/save'
  ): Promise<FinalLoanApplication> {
    const url = `${this.api.baseUrl}${endpoint}`;

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    this.errorMessageService.clearErrorMessages();

    return await firstValueFrom(
      this.http.post<FinalLoanApplication>(url, body, {
        headers,
      })
    );
  }

  /**
   * Use this to update the {FinalLoanApplication} after getting a response
   * from POST. This will update the local object with ID's.
   *
   * @param loan Entire loan object, {FinalLoanApplication}.
   */
  setLoanAfterPost(loan: FinalLoanApplication) {
    this.finalLoanApplication.next({ ...loan });
  }

  /**
   * Consumer Loan Part #1 - Initilize loan.
   *
   * @param date Form data of type {LoanApplication}.
   */
  setGetStarted(data: LoanApplication | null) {
    this.finalLoanApplication.next({
      ...this.finalLoanApplication.value,
      LoanApplication: {
        ...this.finalLoanApplication.value?.LoanApplication,
        RequestZip: data?.RequestZip,
        RequestAmount: data?.RequestAmount,
        RequestReferCD: data?.RequestReferCD,
        RequestPurpose: data?.RequestPurpose,
        RequestCreditTypeCD: data?.RequestCreditTypeCD,
      },
    });
  }

  /**
   * Consumer Loan Part #2 - Branch selection.
   *
   * @param domainId This is the selected branch ID.
   */
  setBranchSelection(domainId: number | null) {
    this.finalLoanApplication.next({
      ...this.finalLoanApplication.value,
      LoanApplication: {
        ...this.finalLoanApplication.value?.LoanApplication,
        DomainID: domainId,
      },
    });
  }

  /**
   * Consumer Loan Part #3 and #5a - Applicant or Co-Applicant Information.
   * Updates Observable with form data.
   *
   * @param data Form data of type {ApplicantInformationForm}.
   * @param coApplicant Defaults to `false`. If `true` the data will save as co-applicant.
   * @returns Transformed {FinalLoanApplication} object to use for POST to server.
   */
  setApplicantInformation(
    data: ApplicantInformationForm,
    coApplicant = false
  ): FinalLoanApplication {
    const key = coApplicant ? 'CoApplicant' : 'Applicant';
    const addressKey = coApplicant ? 'CoApplicantAddress' : 'ApplicantAddress';
    const employmentKey = coApplicant
      ? 'CoApplicantEmployment'
      : 'ApplicantEmployment';

    this.finalLoanApplication.next({
      ...this.finalLoanApplication.value,
      [key]: {
        ...this.finalLoanApplication.value?.[key],
        ContactFirst: data.ContactFirst,
        ContactLast: data.ContactLast,
        ContactMiddle: data.ContactMiddle,
        ContactSuffix: data.ContactSuffix,
        MaritalStatusCD: data.MaritalStatusCD,
        Dependents: data.Dependents,
        PhoneNumber: stripPhoneNumber(data.PhoneNumber),
        PhoneNumberType: data.PhoneNumberType,
        EmailAddress: data.EmailAddress,
      },
      [addressKey]: {
        ...this.finalLoanApplication.value?.[addressKey],
        Address: data.Address,
        AptNumber: data.AptNumber,
        POBox: data.POBox,
        City: data.City,
        State: data.State,
        ZipCode: data.ZipCode,
        Years: data.Years,
        Months: data.Months,
        LivingSituation: data.LivingSituation,
      },
      [employmentKey]: {
        ...this.finalLoanApplication.value?.[employmentKey],
        EmploymentStatus: data.EmploymentStatus,
      },
    });

    if (!coApplicant) {
      this.finalLoanApplication.next({
        ...this.finalLoanApplication.value,
        LoanApplication: {
          ...this.finalLoanApplication.value?.LoanApplication,
          Over18: data.Over18,
          PrivacyPolicy: data.PrivacyPolicy,
          CommunicationConsent: data.CommunicationConsent,
        },
      });

      this.ApplicantOwnAnyVehicles.next({
        NumberOfVehicles: data.NumberOfVehicles,
        OwnAnyVehicles: data.OwnAnyVehicles,
      });
    } else {
      this.CoApplicantOwnAnyVehicles.next({
        NumberOfVehicles: data.NumberOfVehicles,
        OwnAnyVehicles: data.OwnAnyVehicles,
      });
    }

    return this.finalLoanApplication.value!;
  }

  /**
   * Consumer Loan Part #4 and #5b -  Applicant or Co-Applicant Rent & Vehicles.
   *
   * @param data Form data of type {ApplicantRentForm}.
   * @param coApplicant Defaults to `false`. If `true` the data will save as co-applicant.
   * @returns Transformed {FinalLoanApplication} object to use for POST to server.
   */
  setApplicantRent(
    data: ApplicantRentForm,
    coApplicant = false
  ): FinalLoanApplication {
    const key = coApplicant ? 'CoApplicantAddress' : 'ApplicantAddress';

    const vehicleKey = coApplicant
      ? 'CoApplicantVehicles'
      : 'ApplicantVehicles';

    const housingKey = coApplicant ? 'CoApplicantHousing' : 'ApplicantHousing';

    const livingSituation =
      this.finalLoanApplication.value?.[key]?.LivingSituation;

    if (livingSituation === LivingSituationType.rent) {
      this.finalLoanApplication.next({
        ...this.finalLoanApplication.value,
        [housingKey]: {
          ...this.finalLoanApplication.value?.[housingKey],
          HousingContactName: data.HousingContactName,
          HousingContactPhone: stripPhoneNumber(data.HousingContactPhone),
          MonthlyCost: data.MonthlyCost,
        },
      });
    } else if (livingSituation === LivingSituationType.own) {
      this.finalLoanApplication.next({
        ...this.finalLoanApplication.value,
        [housingKey]: {
          ...this.finalLoanApplication.value?.[housingKey],
          MortgageBalance: data.MortgageBalance,
          Lienholder: data.Lienholder,
          MarketValue: +data.MarketValue!,
        },
      });
    } else if (livingSituation === LivingSituationType.parent) {
      this.finalLoanApplication.next({
        ...this.finalLoanApplication.value,
        [housingKey]: {
          ...this.finalLoanApplication.value?.[housingKey],
          MonthlyCost: data.MonthlyCost,
        },
      });
    }

    // This will be removed and readded with each edit on the server.
    // No need to capture previous values.
    this.finalLoanApplication.next({
      ...this.finalLoanApplication.value,
      [vehicleKey]: data?.ApplicantVehicles,
    });

    return this.finalLoanApplication.value!;
  }

  /**
   * Consumer Loan Part #5 and #5c - Applicant or Co-Applicant Employer.
   *
   * @param data Form data of type {ApplicantEmployerForm}.
   * @param coApplicant Defaults to `false`. If `true` the data will save as co-applicant.
   * @returns Transformed {FinalLoanApplication} object to use for POST to server.
   */
  setApplicantEmployer(
    data: ApplicantEmployerForm,
    coApplicant = false
  ): FinalLoanApplication {
    const key = coApplicant ? 'CoApplicantEmployment' : 'ApplicantEmployment';
    const incomeKey = coApplicant
      ? 'CoApplicantAdditionalIncome'
      : 'ApplicantAdditionalIncome';

    this.finalLoanApplication.next({
      ...this.finalLoanApplication.value,
      [key]: {
        ...this.finalLoanApplication.value?.[key],
        EmployerName: data.EmployerName,
        Position: data.Position,
        Years: data.Years,
        Months: data.Months,
        Address: data.Address,
        City: data.City,
        State: data.State,
        ZipCode: data.ZipCode,
        PhoneNumber: stripPhoneNumber(data.PhoneNumber),
        PayTypeCD: data.PayTypeCD,
        PayPeriod: data.PayPeriod,
        TakeHomePay: data.TakeHomePay,
        AnnualSalary: data.AnnualSalary ?? null,
        HourlyRate: data.HourlyRate ?? null,
        HoursPerWeek: data.HoursPerWeek ?? null,
      },
      [incomeKey]: {
        ...this.finalLoanApplication.value?.[incomeKey],
        OtherIncomes: data.OtherIncomes,
        MonthlyAdditionalIncome: data.MonthlyAdditionalIncome,
      },
    });

    return this.finalLoanApplication.value!;
  }

  /**
   * Consumer Loan Part #6 - Applicant Banking.
   *
   * @param data Applicant form data of type {ApplicantBankingForm}.
   * @param coApplicantData Co-applicant form data of type {ApplicantBankingForm}.
   * @returns Transformed {FinalLoanApplication} object to use for POST to server.
   */
  setApplicantBanking(
    data: ApplicantBankingForm,
    coApplicantData?: ApplicantBankingForm | null
  ): FinalLoanApplication {
    this.finalLoanApplication.next({
      ...this.finalLoanApplication.value,
      Applicant: {
        ...this.finalLoanApplication.value?.Applicant,
        DateOfBirth: data.DateOfBirth,
        SSN: data.SSN,
      },
      LoanApplication: {
        ...this.finalLoanApplication.value?.LoanApplication,
        CreditReportAuthorization: data.CreditReportAuthorization,
        DisclaimerForOhioApplicants: data.DisclaimerForOhioApplicants,
      },
      ApplicantBanking: {
        ...this.finalLoanApplication.value?.ApplicantBanking,
        CheckingAccountStatus: data.CheckingAccountStatus,
        CheckingBankName: data.CheckingBankName,
        HasDebitCard: data.HasDebitCard,
      },
    });

    if (coApplicantData) {
      this.finalLoanApplication.next({
        ...this.finalLoanApplication.value,
        CoApplicant: {
          ...this.finalLoanApplication.value?.CoApplicant,
          DateOfBirth: coApplicantData.DateOfBirth,
          SSN: coApplicantData.SSN,
        },
        CoApplicantBanking: {
          ...this.finalLoanApplication.value?.CoApplicantBanking,
          CheckingAccountStatus: coApplicantData.CheckingAccountStatus,
          CheckingBankName: coApplicantData.CheckingBankName,
          HasDebitCard: coApplicantData.HasDebitCard,
        },
      });
    }

    return this.finalLoanApplication.value!;
  }

  /**
   * When a user exits the form to finish by phone.
   * This updates the loan application with the user's confirmed phone number.
   *
   * @param phone Confirmed phone number from exit modal screen.
   */
  setPhoneNumberOnExit(phone: string) {
    this.finalLoanApplication.next({
      ...this.finalLoanApplication.value,
      Applicant: {
        ...this.finalLoanApplication.value?.Applicant,
        PhoneNumber: stripPhoneNumber(phone),
      },
    });
  }

  setCoApplicantAddressSameAsPrimary(isSame = true) {
    if (isSame) {
      const address = this.finalLoanApplication.value?.ApplicantAddress;

      this.finalLoanApplication.next({
        ...this.finalLoanApplication.value,
        CoApplicantAddress: {
          ...this.finalLoanApplication.value?.CoApplicantAddress,
          Address: address?.Address,
          AptNumber: address?.AptNumber,
          POBox: address?.POBox,
          City: address?.City,
          State: address?.State,
          ZipCode: address?.ZipCode,
        },
      });

      return;
    }

    this.finalLoanApplication.next({
      ...this.finalLoanApplication.value,
      CoApplicantAddress: {
        ...this.finalLoanApplication.value?.CoApplicantAddress,
        Address: '',
        AptNumber: '',
        POBox: '',
        City: '',
        State: '',
        ZipCode: '',
      },
    });
  }

  /**
   * Only call this function if employment has changed to `unemployed` or `retired`. It will clear out
   * all employment information for applicant or co-applicant.
   *
   * @param employmentStatus Employment status.
   * @param coApplicant Defaults to `false`.
   */
  clearOnEmploymentStatusChange(employmentStatus: string, coApplicant = false) {
    const key = coApplicant ? 'CoApplicantEmployment' : 'ApplicantEmployment';
    const otherKey = coApplicant
      ? 'CoApplicantAdditionalIncome'
      : 'ApplicantAdditionalIncome';

    const loan = {
      ...this.finalLoanApplication.value,
    };

    delete loan?.[key];
    delete loan?.[otherKey];

    this.finalLoanApplication.next({
      ...loan,
      [key]: {
        EmploymentStatus: employmentStatus,
      },
    });
  }

  clearOnVehiclesOwnedChange(coApplicant = false) {
    const key = coApplicant ? 'CoApplicantVehicles' : 'ApplicantVehicles';

    this.finalLoanApplication.next({
      ...this.finalLoanApplication.value,
      [key]: [],
    });
  }

  /**
   * Clears out the following objects; for applicant or co-applicant, on change event:
   * * ApplicantAddress or CoApplicantAddress
   * * ApplicantHousing or CoApplicantHousing
   */
  clearOnLivingSituationChange(livingSituation: string, coApplicant = false) {
    const key = coApplicant ? 'CoApplicantAddress' : 'ApplicantAddress';
    const housingKey = coApplicant ? 'CoApplicantHousing' : 'ApplicantHousing';

    const loan = {
      ...this.finalLoanApplication.value,
      [key]: {
        ...this.finalLoanApplication.value?.[key],
        LivingSituation: livingSituation,
      },
    };

    delete loan?.[housingKey];

    this.finalLoanApplication.next(loan);
  }

  /**
   * Clears out all co-applicant data if switching to apply individually.
   */
  clearCoApplicantOnApplicationTypeChange(applicationType: string) {
    if (applicationType === RequestCreditType.individualy) {
      const loan = {
        ...this.finalLoanApplication.value,
        LoanApplication: {
          ...this.finalLoanApplication.value?.LoanApplication,
          RequestCreditTypeCD: applicationType,
        },
      };

      delete loan?.CoApplicant;
      delete loan?.CoApplicantAdditionalIncome;
      delete loan?.CoApplicantAddress;
      delete loan?.CoApplicantHousing;
      delete loan?.CoApplicantEmployment;
      delete loan?.CoApplicantVehicles;

      this.finalLoanApplication.next(loan);
      this.CoApplicantOwnAnyVehicles.next(null);
    }
  }

  /**
   * Clears out the following properties; for applicant or co-applicant, on change event:
   * * AnnualSalary
   * * HoursPerWeek
   * * HourlyRate
   */
  clearOnPayTypeChange(payType: string, coApplicant = false) {
    const key = coApplicant ? 'CoApplicantEmployment' : 'ApplicantEmployment';

    const loan = {
      ...this.finalLoanApplication.value,
      [key]: {
        ...this.finalLoanApplication.value?.[key],
        PayTypeCD: payType,
        AnnualSalary: null,
        HoursPerWeek: null,
        HourlyRate: null,
      },
    };

    this.finalLoanApplication.next(loan);
  }

  /**
   * This is called when the searched zip code is out of the service range and
   * the user exits the form.
   *
   * Can also be called when user confirmed to finish application by phone or after
   * the application has been submitted.
   */
  clearApplication() {
    this.finalLoanApplication.next(null);
    this.ApplicantOwnAnyVehicles.next(null);
    this.CoApplicantOwnAnyVehicles.next(null);
  }
}
